import React, { useMemo, useState } from "react";
import {
  Button,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
  Collapse,
} from "@material-ui/core";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { ace } from "../theme";
import ITEM_CFT_MAPPING from "../../../constants/itemCftMapping";
import { getCftValueForItem } from "../../../utils/cftCalculator";

function buildPayloadItem(row) {
  const name = (row.item || "").trim();
  if (!name) return null;
  const qty = Math.max(0, Math.floor(Number(row.quantity) || 0));
  const match = getCftValueForItem(name, ITEM_CFT_MAPPING);
  const cftPerItem = match.matched && typeof match.cftPerItem === "number" ? match.cftPerItem : 0;
  return {
    itemName: name,
    normalizedItemName: match.normalizedItem || row.normalizedItem || "",
    quantity: qty,
    cftPerItem,
    matched: !!match.matched,
    matchStatus: match.matched ? "Matched" : "Unmatched",
    rawImportedItemName: name,
    notes: "",
  };
}

const InventoryStep = ({ rows, setRows, vehicles, setVehicles, submitted }) => {
  const [openSuggest, setOpenSuggest] = useState({});

  const optionList = useMemo(
    () => Object.keys(ITEM_CFT_MAPPING).sort((a, b) => a.localeCompare(b)),
    []
  );

  const addRow = () => {
    setRows((prev) => [...prev, { id: Date.now(), item: "", quantity: "1", normalizedItem: "" }]);
  };

  const removeRow = (id) => {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.id !== id)));
  };

  const updateRow = (id, patch) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const filterOptions = (input) => {
    const q = (input || "").trim().toLowerCase();
    if (!q) return optionList;
    return optionList.filter((o) => o.toLowerCase().includes(q));
  };

  return (
    <div>
      <Typography variant="h5" style={{ color: ace.black, fontWeight: 700, marginBottom: 8 }}>
        Your inventory
      </Typography>
      <Typography variant="body2" style={{ color: ace.muted, marginBottom: 24 }}>
        Add each item and quantity. Choose from suggestions or type your own — both are fine.
      </Typography>
      {rows.map((row) => (
        <Paper
          key={row.id}
          elevation={0}
          style={{
            border: `1px solid ${ace.grayBorder}`,
            borderRadius: 12,
            padding: 16,
            marginBottom: 12,
            position: "relative",
          }}
        >
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Item"
                value={row.item}
                onChange={(e) => {
                  const val = e.target.value;
                  updateRow(row.id, { item: val });
                  setOpenSuggest((s) => ({ ...s, [row.id]: true }));
                }}
                onFocus={() => setOpenSuggest((s) => ({ ...s, [row.id]: true }))}
                onBlur={() => {
                  setTimeout(() => setOpenSuggest((s) => ({ ...s, [row.id]: false })), 200);
                }}
                disabled={submitted}
                placeholder="e.g. Sofa 3 seater"
              />
              <Collapse in={!!openSuggest[row.id] && !submitted}>
                <Paper
                  style={{
                    marginTop: 4,
                    maxHeight: "min(360px, 50vh)",
                    overflow: "auto",
                    zIndex: 2,
                  }}
                >
                  {filterOptions(row.item).map((opt) => (
                    <div
                      key={opt}
                      role="button"
                      tabIndex={0}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        const match = getCftValueForItem(opt, ITEM_CFT_MAPPING);
                        updateRow(row.id, {
                          item: opt,
                          normalizedItem: match.normalizedItem || opt,
                        });
                        setOpenSuggest((s) => ({ ...s, [row.id]: false }));
                      }}
                      style={{
                        padding: "10px 12px",
                        cursor: "pointer",
                        fontSize: 14,
                        borderBottom: `1px solid ${ace.grayBorder}`,
                      }}
                    >
                      {opt}
                    </div>
                  ))}
                </Paper>
              </Collapse>
            </Grid>
            <Grid item xs={8} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                label="Quantity"
                type="number"
                inputProps={{ min: 0 }}
                value={row.quantity}
                onChange={(e) => updateRow(row.id, { quantity: e.target.value })}
                disabled={submitted}
              />
            </Grid>
            <Grid item xs={4} md={2} style={{ textAlign: "right" }}>
              <IconButton
                aria-label="Remove row"
                onClick={() => removeRow(row.id)}
                disabled={submitted || rows.length <= 1}
                style={{ marginTop: 8 }}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Grid>
          </Grid>
        </Paper>
      ))}
      <Button
        startIcon={<AddCircleOutlineIcon />}
        onClick={addRow}
        disabled={submitted}
        style={{ color: ace.red, marginTop: 8 }}
      >
        Add another item
      </Button>

      <Typography
        variant="h6"
        style={{ color: ace.black, fontWeight: 700, marginTop: 32, marginBottom: 8 }}
      >
        Vehicles (optional)
      </Typography>
      <Typography variant="body2" style={{ color: ace.muted, marginBottom: 16 }}>
        Add any vehicles we should know about — make, model, registration, etc. Skip if none.
      </Typography>
      {vehicles.map((v, idx) => (
        <Grid container spacing={1} key={`veh-${idx}`} alignItems="center" style={{ marginBottom: 8 }}>
          <Grid item xs={11}>
            <TextField
              fullWidth
              variant="outlined"
              label={`Vehicle ${idx + 1}`}
              size="small"
              value={v}
              placeholder="e.g. Honda City, KA01 AB 1234"
              onChange={(e) => {
                const next = [...vehicles];
                next[idx] = e.target.value;
                setVehicles(next.length ? next : [""]);
              }}
              disabled={submitted}
            />
          </Grid>
          <Grid item xs={1} style={{ textAlign: "right" }}>
            <IconButton
              aria-label="Remove vehicle"
              size="small"
              onClick={() =>
                setVehicles(
                  vehicles.filter((_, i) => i !== idx).length
                    ? vehicles.filter((_, i) => i !== idx)
                    : [""]
                )
              }
              disabled={submitted}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Grid>
        </Grid>
      ))}
      <Button
        startIcon={<AddCircleOutlineIcon />}
        onClick={() => setVehicles([...vehicles.map((x) => String(x)), ""])}
        disabled={submitted}
        size="small"
        style={{ color: ace.charcoal, marginTop: 4 }}
      >
        Add vehicle
      </Button>
    </div>
  );
};

export default InventoryStep;
export { buildPayloadItem };
